import { addDays, addHours, addMinutes, addMonths, addQuarters, addSeconds, addWeeks, addYears, endOfDay, endOfHour, endOfMinute, endOfMonth, endOfSecond, endOfWeek, isValid, setMonth, startOfDay, startOfHour, startOfMinute, startOfMonth, startOfSecond, startOfWeek, } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { FilterType } from '../Model';
import { DatePart } from '../Model/Formula/Funcs/DateFunc';
export var FilterValidity;
(function (FilterValidity) {
    FilterValidity[FilterValidity["InvalidFromAfterTo"] = 0] = "InvalidFromAfterTo";
    FilterValidity[FilterValidity["InvalidStart"] = 1] = "InvalidStart";
    FilterValidity[FilterValidity["InvalidEnd"] = 2] = "InvalidEnd";
    FilterValidity[FilterValidity["Invalid"] = 3] = "Invalid";
    FilterValidity[FilterValidity["Valid"] = 4] = "Valid";
})(FilterValidity || (FilterValidity = {}));
/**
 * Returns true if the filter is valid, false otherwise
 */
export function isValidFilter(filter) {
    return validateFilter(filter) === FilterValidity.Valid;
}
/**
 * Returns {@link FilterValidity.Valid} for relative date filters with a valid date range. This means
 * that a from/to date (if present) must 1) be a valid date and 2) when both from/to are specified, the
 * start date may not fall after the end date
 */
export function validateFilter(filter) {
    if (filter == null) {
        return FilterValidity.Invalid;
    }
    let range;
    try {
        range = getDateRange(filter);
    }
    catch {
        // handled below
    }
    // Anchor or fixed value is invalid.
    // todo: maybe return a code for these cases specifically
    if (range === undefined) {
        return FilterValidity.Invalid;
    }
    // Invalid start
    if (!(range.from === undefined || isValid(range.from))) {
        return FilterValidity.InvalidStart;
    }
    // Invalid end
    if (!(range.to === undefined || isValid(range.to))) {
        return FilterValidity.InvalidEnd;
    }
    // Start cannot be after end
    if (isValid(range.from) && isValid(range.to)) {
        if (+range.from > +range.to) {
            return FilterValidity.InvalidFromAfterTo;
        }
    }
    return FilterValidity.Valid;
}
/**
 * Converts an EncRelativeDateFilterField to a EncContinuousDateFilterField
 */
export function toContinuousDateFilterField(filter) {
    return {
        ...toContinuousDateFilter(filter),
        id: filter.id,
        formula: filter.formula,
        variable: filter.variable,
    };
}
/**
 * Converts an EncRelativeDateFilter(Field) to a EncContinuousDateFilter
 */
export function toContinuousDateFilter(filter) {
    const range = getDateRange(filter);
    const min = range.from ? +range.from : null;
    const max = range.to ? +range.to : null;
    return {
        filterType: FilterType.ContinuousDate,
        includeNulls: filter.includeNulls,
        includeMin: true,
        includeMax: true,
        range: {
            min,
            max,
        },
    };
}
/**
 * Returns the anchor date (`Date.now()` if not provided by the filter) along with
 * the from/to dates from the filter config; from/to may be undefined if either has
 * been configured as "unset"
 */
export function getDateRange(filter) {
    var _a;
    const anchor = new Date((_a = filter.anchor) !== null && _a !== void 0 ? _a : Date.now());
    const from = getDate(anchor, filter.from, filter.weekStart, filter.yearStart);
    const to = getDate(anchor, filter.to, filter.weekStart, filter.yearStart);
    return {
        anchor,
        from,
        to,
    };
}
export function getDate(anchor, fromto, weekStart = 0, yearStart = 1) {
    if (fromto.type === 'unset') {
        return undefined;
    }
    if (fromto.type === 'fixed') {
        const date = new Date(fromto.value);
        if (!isValid(date)) {
            throw new Error(`Invalid fixed date: ${fromto.value}`);
        }
        return date;
    }
    if (!isValid(anchor)) {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        throw new Error(`Invalid anchor: ${anchor}`);
    }
    let date = utcToZonedTime(anchor, 'Z');
    date = dateAdd(date, fromto.part, fromto.relTo, fromto.value);
    date = dateTrunc(date, fromto.part, fromto.clamp, weekStart, yearStart);
    date = zonedTimeToUtc(date, 'Z');
    return date;
}
function dateAdd(date, datePart, relTo, value) {
    const amount = relTo === 'previous' ? -value : relTo === 'next' ? value : 0;
    if (amount === 0) {
        return date;
    }
    switch (datePart) {
        case DatePart.YEAR:
            return addYears(date, amount);
        case DatePart.QUARTER:
            return addQuarters(date, amount);
        case DatePart.MONTH:
            return addMonths(date, amount);
        case DatePart.WEEK:
            return addWeeks(date, amount);
        case DatePart.DAY:
        case DatePart.DAYOFYEAR:
        case DatePart.WEEKDAY:
            return addDays(date, amount);
        case DatePart.HOUR:
            return addHours(date, amount);
        case DatePart.MINUTE:
            return addMinutes(date, amount);
        case DatePart.SECOND:
            return addSeconds(date, amount);
    }
}
function dateTrunc(date, datePart, clampTo, weekStart, yearStart) {
    // Start of
    if (clampTo === 'start') {
        switch (datePart) {
            case DatePart.YEAR: {
                const startMonth = yearStart - 1;
                const shiftedDate = addMonths(date, -startMonth);
                return startOfMonth(setMonth(shiftedDate, startMonth));
            }
            case DatePart.QUARTER: {
                const month = date.getMonth();
                const quarters = getQuarters(yearStart);
                const distanceToStart = getQuarterStartOffset(quarters, month);
                return startOfMonth(addMonths(date, -distanceToStart));
            }
            case DatePart.MONTH:
                return startOfMonth(date);
            case DatePart.WEEK:
                return startOfWeek(date, {
                    weekStartsOn: weekStart,
                });
            case DatePart.DAY:
            case DatePart.DAYOFYEAR:
            case DatePart.WEEKDAY:
                return startOfDay(date);
            case DatePart.HOUR:
                return startOfHour(date);
            case DatePart.MINUTE:
                return startOfMinute(date);
            case DatePart.SECOND:
                return startOfSecond(date);
        }
    }
    // End of
    switch (datePart) {
        case DatePart.YEAR: {
            const startMonth = dateTrunc(date, datePart, 'start', weekStart, yearStart);
            return endOfMonth(addMonths(startMonth, 11));
        }
        case DatePart.QUARTER: {
            const month = date.getMonth();
            const quarters = getQuarters(yearStart);
            const distanceToEnd = getQuarterEndOffset(quarters, month);
            return endOfMonth(addMonths(date, distanceToEnd));
        }
        case DatePart.MONTH:
            return endOfMonth(date);
        case DatePart.WEEK:
            return endOfWeek(date, {
                weekStartsOn: weekStart,
            });
        case DatePart.DAY:
        case DatePart.DAYOFYEAR:
        case DatePart.WEEKDAY:
            return endOfDay(date);
        case DatePart.HOUR:
            return endOfHour(date);
        case DatePart.MINUTE:
            return endOfMinute(date);
        case DatePart.SECOND:
            return endOfSecond(date);
    }
}
const quartersByFiscalYearStartMonth = {
    1: [1, 1, 1, 2, 2, 2, 3, 3, 3, 4, 4, 4],
    2: [4, 1, 1, 1, 2, 2, 2, 3, 3, 3, 4, 4],
    3: [4, 4, 1, 1, 1, 2, 2, 2, 3, 3, 3, 4],
    4: [4, 4, 4, 1, 1, 1, 2, 2, 2, 3, 3, 3],
    5: [3, 4, 4, 4, 1, 1, 1, 2, 2, 2, 3, 3],
    6: [3, 3, 4, 4, 4, 1, 1, 1, 2, 2, 2, 3],
    7: [3, 3, 3, 4, 4, 4, 1, 1, 1, 2, 2, 2],
    8: [2, 3, 3, 3, 4, 4, 4, 1, 1, 1, 2, 2],
    9: [2, 2, 3, 3, 3, 4, 4, 4, 1, 1, 1, 2],
    10: [2, 2, 2, 3, 3, 3, 4, 4, 4, 1, 1, 1],
    11: [1, 2, 2, 2, 3, 3, 3, 4, 4, 4, 1, 1],
    12: [1, 1, 2, 2, 2, 3, 3, 3, 4, 4, 4, 1],
};
function getQuarters(yearStart = 1) {
    const quarters = quartersByFiscalYearStartMonth[yearStart];
    if (quarters === undefined) {
        throw new Error(`getQuarters(): expected 'yearStart' to be an int between 1 and 12, saw: ${yearStart}`);
    }
    return quarters;
}
function getQuarterStartOffset(quarters, start) {
    var _a;
    return (_a = getQuarterBoundaryOffset(quarters, start, -1)) !== null && _a !== void 0 ? _a : 0;
}
function getQuarterEndOffset(quarters, start) {
    var _a;
    return (_a = getQuarterBoundaryOffset(quarters, start, +1)) !== null && _a !== void 0 ? _a : 0;
}
function getQuarterBoundaryOffset(quarters, month, dir) {
    const length = quarters.length;
    const target = quarters[month];
    for (let i = 0; i < length; i++) {
        const index = (length + month + i * dir) % length;
        if (quarters[index] !== target) {
            return i - 1;
        }
    }
}
